import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import "./GetAQuote.css";
import axios from "axios";
import toast from "react-hot-toast";

const CountryDropdown = ({ countries, selectedCountry, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.includes(searchTerm)
  );

  return (
    <div className="gaq-country-dropdown-container" ref={dropdownRef}>
      <div className="gaq-country-selector" onClick={() => setIsOpen(!isOpen)}>
        <span className={`fi fi-${selectedCountry.iso}`}></span>
        <span className="gaq-country-code">{selectedCountry.code}</span>
        <ChevronDownIcon
          className={`gaq-dropdown-icon ${isOpen ? "gaq-rotate-180" : ""}`}
        />
      </div>

      {isOpen && (
        <div className="gaq-country-dropdown-menu">
          <div className="gaq-country-search-container">
            <input
              type="text"
              placeholder="Search countries..."
              className="gaq-country-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="gaq-country-list">
            {filteredCountries.map((country) => (
              <div
                key={country.iso}
                className={`gaq-country-item ${
                  selectedCountry.iso === country.iso ? "gaq-selected" : ""
                }`}
                onClick={() => {
                  onSelect(country);
                  setIsOpen(false);
                  setSearchTerm("");
                }}
              >
                <span className={`fi fi-${country.iso}`}></span>
                <span className="gaq-country-name">{country.name}</span>
                <span className="gaq-country-code">{country.code}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const GetAQuote = ({ showModal, setShowModal }) => {
  const [step, setStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState({
    name: "United States",
    code: "+1",
    iso: "us",
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    serviceType: "",
    message: "",
  });

  const countries = [
    { name: "United States", code: "+1", iso: "us" },
    { name: "United Kingdom", code: "+44", iso: "gb" },
    { name: "Canada", code: "+1", iso: "ca" },
    { name: "Australia", code: "+61", iso: "au" },
    { name: "Germany", code: "+49", iso: "de" },
    { name: "France", code: "+33", iso: "fr" },
    { name: "Japan", code: "+81", iso: "jp" },
    { name: "India", code: "+91", iso: "in" },
    { name: "Brazil", code: "+55", iso: "br" },
    { name: "China", code: "+86", iso: "cn" },
    { name: "South Africa", code: "+27", iso: "za" },
    { name: "Nigeria", code: "+234", iso: "ng" },
    { name: "United Arab Emirates", code: "+971", iso: "ae" },
    { name: "Saudi Arabia", code: "+966", iso: "sa" },
    { name: "Singapore", code: "+65", iso: "sg" },
    { name: "Malaysia", code: "+60", iso: "my" },
    { name: "Indonesia", code: "+62", iso: "id" },
    { name: "Philippines", code: "+63", iso: "ph" },
    { name: "Vietnam", code: "+84", iso: "vn" },
    { name: "Thailand", code: "+66", iso: "th" },
    { name: "South Korea", code: "+82", iso: "kr" },
    { name: "Russia", code: "+7", iso: "ru" },
    { name: "Italy", code: "+39", iso: "it" },
    { name: "Spain", code: "+34", iso: "es" },
    { name: "Netherlands", code: "+31", iso: "nl" },
    { name: "Switzerland", code: "+41", iso: "ch" },
    { name: "Sweden", code: "+46", iso: "se" },
    { name: "Norway", code: "+47", iso: "no" },
    { name: "Denmark", code: "+45", iso: "dk" },
    { name: "Finland", code: "+358", iso: "fi" },
    { name: "Poland", code: "+48", iso: "pl" },
    { name: "Mexico", code: "+52", iso: "mx" },
    { name: "Argentina", code: "+54", iso: "ar" },
    { name: "Chile", code: "+56", iso: "cl" },
    { name: "Colombia", code: "+57", iso: "co" },
    { name: "Peru", code: "+51", iso: "pe" },
    { name: "Turkey", code: "+90", iso: "tr" },
    { name: "Egypt", code: "+20", iso: "eg" },
    { name: "Israel", code: "+972", iso: "il" },
    { name: "Pakistan", code: "+92", iso: "pk" },
    { name: "Bangladesh", code: "+880", iso: "bd" },
    { name: "New Zealand", code: "+64", iso: "nz" },
    { name: "Ireland", code: "+353", iso: "ie" },
    { name: "Portugal", code: "+351", iso: "pt" },
    { name: "Greece", code: "+30", iso: "gr" },
    { name: "Czech Republic", code: "+420", iso: "cz" },
    { name: "Hungary", code: "+36", iso: "hu" },
    { name: "Austria", code: "+43", iso: "at" },
    { name: "Belgium", code: "+32", iso: "be" },
    { name: "Romania", code: "+40", iso: "ro" },
    { name: "Ukraine", code: "+380", iso: "ua" },
    { name: "Kenya", code: "+254", iso: "ke" },
    { name: "Ghana", code: "+233", iso: "gh" },
    { name: "Ethiopia", code: "+251", iso: "et" },
    { name: "Morocco", code: "+212", iso: "ma" },
    { name: "Algeria", code: "+213", iso: "dz" },
    { name: "Tunisia", code: "+216", iso: "tn" },
    { name: "Iraq", code: "+964", iso: "iq" },
    { name: "Iran", code: "+98", iso: "ir" },
    { name: "Afghanistan", code: "+93", iso: "af" },
    { name: "Sri Lanka", code: "+94", iso: "lk" },
    { name: "Nepal", code: "+977", iso: "np" },
    { name: "Cambodia", code: "+855", iso: "kh" },
    { name: "Myanmar", code: "+95", iso: "mm" },
    { name: "Laos", code: "+856", iso: "la" },
    { name: "Mongolia", code: "+976", iso: "mn" },
    { name: "Kazakhstan", code: "+7", iso: "kz" },
    { name: "Uzbekistan", code: "+998", iso: "uz" },
    { name: "Azerbaijan", code: "+994", iso: "az" },
    { name: "Georgia", code: "+995", iso: "ge" },
    { name: "Armenia", code: "+374", iso: "am" },
    { name: "Belarus", code: "+375", iso: "by" },
    { name: "Moldova", code: "+373", iso: "md" },
    { name: "Latvia", code: "+371", iso: "lv" },
    { name: "Lithuania", code: "+370", iso: "lt" },
    { name: "Estonia", code: "+372", iso: "ee" },
    { name: "Slovenia", code: "+386", iso: "si" },
    { name: "Croatia", code: "+385", iso: "hr" },
    { name: "Serbia", code: "+381", iso: "rs" },
    { name: "North Macedonia", code: "+389", iso: "mk" },
    { name: "Albania", code: "+355", iso: "al" },
    { name: "Montenegro", code: "+382", iso: "me" },
    { name: "Bosnia and Herzegovina", code: "+387", iso: "ba" },
    { name: "Bulgaria", code: "+359", iso: "bg" },
    { name: "Slovakia", code: "+421", iso: "sk" },
    { name: "Luxembourg", code: "+352", iso: "lu" },
    { name: "Iceland", code: "+354", iso: "is" },
    { name: "Malta", code: "+356", iso: "mt" },
    { name: "Cyprus", code: "+357", iso: "cy" },
    { name: "Lebanon", code: "+961", iso: "lb" },
    { name: "Jordan", code: "+962", iso: "jo" },
    { name: "Qatar", code: "+974", iso: "qa" },
    { name: "Kuwait", code: "+965", iso: "kw" },
    { name: "Oman", code: "+968", iso: "om" },
    { name: "Bahrain", code: "+973", iso: "bh" },
    { name: "Yemen", code: "+967", iso: "ye" },
    { name: "Syria", code: "+963", iso: "sy" },
    { name: "Palestine", code: "+970", iso: "ps" },
    { name: "Libya", code: "+218", iso: "ly" },
    { name: "Sudan", code: "+249", iso: "sd" },
    { name: "South Sudan", code: "+211", iso: "ss" },
    { name: "Somalia", code: "+252", iso: "so" },
    { name: "Djibouti", code: "+253", iso: "dj" },
    { name: "Eritrea", code: "+291", iso: "er" },
    { name: "Tanzania", code: "+255", iso: "tz" },
    { name: "Uganda", code: "+256", iso: "ug" },
    { name: "Rwanda", code: "+250", iso: "rw" },
    { name: "Burundi", code: "+257", iso: "bi" },
    { name: "Zambia", code: "+260", iso: "zm" },
    { name: "Zimbabwe", code: "+263", iso: "zw" },
    { name: "Malawi", code: "+265", iso: "mw" },
    { name: "Mozambique", code: "+258", iso: "mz" },
    { name: "Madagascar", code: "+261", iso: "mg" },
    { name: "Mauritius", code: "+230", iso: "mu" },
    { name: "Seychelles", code: "+248", iso: "sc" },
    { name: "Angola", code: "+244", iso: "ao" },
    { name: "Namibia", code: "+264", iso: "na" },
    { name: "Botswana", code: "+267", iso: "bw" },
    { name: "Lesotho", code: "+266", iso: "ls" },
    { name: "Eswatini", code: "+268", iso: "sz" },
    { name: "Costa Rica", code: "+506", iso: "cr" },
    { name: "Panama", code: "+507", iso: "pa" },
    { name: "Dominican Republic", code: "+1", iso: "do" },
    { name: "Puerto Rico", code: "+1", iso: "pr" },
    { name: "Jamaica", code: "+1", iso: "jm" },
    { name: "Trinidad and Tobago", code: "+1", iso: "tt" },
    { name: "Bahamas", code: "+1", iso: "bs" },
    { name: "Barbados", code: "+1", iso: "bb" },
    { name: "Saint Lucia", code: "+1", iso: "lc" },
    { name: "Grenada", code: "+1", iso: "gd" },
    { name: "Saint Vincent and the Grenadines", code: "+1", iso: "vc" },
    { name: "Antigua and Barbuda", code: "+1", iso: "ag" },
    { name: "Dominica", code: "+1", iso: "dm" },
    { name: "Saint Kitts and Nevis", code: "+1", iso: "kn" },
    { name: "Belize", code: "+501", iso: "bz" },
    { name: "El Salvador", code: "+503", iso: "sv" },
    { name: "Guatemala", code: "+502", iso: "gt" },
    { name: "Honduras", code: "+504", iso: "hn" },
    { name: "Nicaragua", code: "+505", iso: "ni" },
    { name: "Ecuador", code: "+593", iso: "ec" },
    { name: "Venezuela", code: "+58", iso: "ve" },
    { name: "Bolivia", code: "+591", iso: "bo" },
    { name: "Paraguay", code: "+595", iso: "py" },
    { name: "Uruguay", code: "+598", iso: "uy" },
    { name: "Guyana", code: "+592", iso: "gy" },
    { name: "Suriname", code: "+597", iso: "sr" },
    { name: "Fiji", code: "+679", iso: "fj" },
    { name: "Papua New Guinea", code: "+675", iso: "pg" },
    { name: "Solomon Islands", code: "+677", iso: "sb" },
    { name: "Vanuatu", code: "+678", iso: "vu" },
    { name: "Samoa", code: "+685", iso: "ws" },
    { name: "Tonga", code: "+676", iso: "to" },
    { name: "Kiribati", code: "+686", iso: "ki" },
    { name: "Marshall Islands", code: "+692", iso: "mh" },
    { name: "Micronesia", code: "+691", iso: "fm" },
    { name: "Palau", code: "+680", iso: "pw" },
    { name: "Nauru", code: "+674", iso: "nr" },
    { name: "Tuvalu", code: "+688", iso: "tv" },
  ];

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showModal]);

  const serviceTypes = [
    "Business Registration",
    "Financial Consulting",
    "Web Development",
    "Digital Marketing",
    "Branding Services",
    "Social Media Strategy",
    "Business Strategy Consulting",
  ];

  const availableTimes = [
    "09:00 AM",
    "10:00 AM",
    "11:00 AM",
    "02:00 PM",
    "03:00 PM",
    "04:00 PM",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 3) {
      setStep(step + 1);
    } else {
      console.log("Consultation scheduled:", formData);

      try {
        const response = await axios.post(
          "https://api.krinvy.com/api/postquoteform",
          formData
        );
        if (response.data.success) {
          toast.success(response.data.message);
          setTimeout(() => {
            setShowModal(false);
            setStep(1);
            setFormData({
              name: "",
              email: "",
              phone: "",
              date: "",
              time: "",
              serviceType: "",
              message: "",
            });
          }, 2000);
        }
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          toast.error(error.response.data.error);
        }
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setFormData((prev) => ({
        ...prev,
        phone: selectedCountry.code + " " + value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
  };

  return (
    <AnimatePresence>
      {showModal && (
        <div className="gaq-modal-overlay gaq-active">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="gaq-modal-container"
          >
            <div className="gaq-modal-body">
              <div className="gaq-modal-content-wrapper">
                {/* Left Side - Content */}
                <div className="gaq-modal-content-left">
                  <h2>Let's create something extraordinary together.</h2>
                  <p>
                    Book a free consultation with our experts to discuss your
                    project ideas
                  </p>
                </div>

                {/* Right Side - Form */}
                <div className="gaq-modal-content-right">
                  <div className="gaq-modal-header">
                    <div className="gaq-modal-header-content">
                      <h3 className="gaq-modal-title">
                        {step === 3
                          ? "Confirm Details"
                          : step === 2
                          ? "Select Date & Time"
                          : "Get a Quote"}
                      </h3>
                      <button
                        className="gaq-modal-close-btn"
                        onClick={() => setShowModal(false)}
                      >
                        <XMarkIcon className="gaq-close-icon" />
                      </button>
                    </div>
                  </div>

                  <div className="gaq-progress-steps">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`gaq-step-indicator ${
                          step >= i ? "gaq-active" : "gaq-inactive"
                        }`}
                      >
                        {i}
                      </div>
                    ))}
                  </div>

                  <form onSubmit={handleSubmit}>
                    {step === 1 && (
                      <div className="gaq-space-y-6">
                        <div className="gaq-form-group">
                          <label className="gaq-form-label">Service Type</label>
                          <select
                            name="serviceType"
                            value={formData.serviceType}
                            onChange={handleChange}
                            className="gaq-form-control"
                            required
                          >
                            <option value="">Select a service</option>
                            {serviceTypes.map((service, index) => (
                              <option key={index} value={service}>
                                {service}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="gaq-form-group">
                          <label className="gaq-form-label">Full Name</label>
                          <div className="gaq-input-with-icon">
                            <UserIcon className="gaq-input-icon w-5 h-5" />
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              className="gaq-form-control"
                              required
                            />
                          </div>
                        </div>

                        <div className="gaq-form-group">
                          <label className="gaq-form-label">Email</label>
                          <div className="gaq-input-with-icon">
                            <EnvelopeIcon className="gaq-input-icon w-5 h-5" />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              className="gaq-form-control"
                              required
                            />
                          </div>
                        </div>

                        <div className="gaq-form-group">
                          <label className="gaq-form-label">Phone Number</label>
                          <div className="gaq-phone-input-container">
                            <CountryDropdown
                              countries={countries}
                              selectedCountry={selectedCountry}
                              onSelect={handleCountrySelect}
                            />
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              className="gaq-phone-number-input"
                              required
                              placeholder="Phone number"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="gaq-space-y-6">
                        <div className="gaq-form-group">
                          <label className="gaq-form-label">
                            Preferred Date
                          </label>
                          <div className="gaq-input-with-icon">
                            <CalendarDaysIcon className="gaq-input-icon w-5 h-5" />
                            <input
                              type="date"
                              name="date"
                              value={formData.date}
                              onChange={handleChange}
                              min={new Date().toISOString().split("T")[0]}
                              className="gaq-form-control"
                              required
                            />
                          </div>
                        </div>

                        <div className="gaq-form-group">
                          <label className="gaq-form-label">
                            Preferred Time
                          </label>
                          <div className="gaq-input-with-icon">
                            <ClockIcon className="gaq-input-icon w-5 h-5" />
                            <select
                              name="time"
                              value={formData.time}
                              onChange={handleChange}
                              className="gaq-form-control"
                              required
                            >
                              <option value="">Select a time</option>
                              {availableTimes.map((time, index) => (
                                <option key={index} value={time}>
                                  {time}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="gaq-form-group">
                          <label className="gaq-form-label">
                            Additional Message
                          </label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={4}
                            className="gaq-form-control"
                            placeholder="Any specific concerns or questions?"
                          />
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <div className="gaq-confirmation-details">
                        <h4 className="gaq-font-semibold gaq-mb-4">
                          Consultation Details
                        </h4>
                        <div className="gaq-space-y-2">
                          <div className="gaq-detail-row">
                            <span className="gaq-detail-label">Service:</span>
                            <span className="gaq-detail-value">
                              {formData.serviceType}
                            </span>
                          </div>
                          <div className="gaq-detail-row">
                            <span className="gaq-detail-label">Name:</span>
                            <span className="gaq-detail-value">
                              {formData.name}
                            </span>
                          </div>
                          <div className="gaq-detail-row">
                            <span className="gaq-detail-label">Email:</span>
                            <span className="gaq-detail-value">
                              {formData.email}
                            </span>
                          </div>
                          <div className="gaq-detail-row">
                            <span className="gaq-detail-label">Phone:</span>
                            <span className="gaq-detail-value">
                              {formData.phone}
                            </span>
                          </div>
                          <div className="gaq-detail-row">
                            <span className="gaq-detail-label">Date:</span>
                            <span className="gaq-detail-value">
                              {formData.date}
                            </span>
                          </div>
                          <div className="gaq-detail-row">
                            <span className="gaq-detail-label">Time:</span>
                            <span className="gaq-detail-value">
                              {formData.time}
                            </span>
                          </div>
                          {formData.message && (
                            <div className="gaq-detail-row">
                              <span className="gaq-detail-label">Message:</span>
                              <span className="gaq-detail-value">
                                {formData.message}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="gaq-form-actions">
                      {step > 1 && (
                        <button
                          type="button"
                          onClick={() => setStep(step - 1)}
                          className="gaq-btn gaq-btn-secondary"
                        >
                          Back
                        </button>
                      )}
                      <button
                        type="submit"
                        className="gaq-btn gaq-btn-primary gaq-ml-auto"
                      >
                        {step === 3 ? "Confirm Booking" : "Continue"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GetAQuote;
